import React, { useState } from "react";
import { apiPost } from "../../services/apiServices";
import { endPoints } from "../../configs/apiUrls";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { login, setUser } from "../../redux/slices/authSlice";
import { FaPhoneAlt, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";

const LoginPage = () => {
  const dispatch = useDispatch();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({ phoneNumber: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const validatePhoneNumber = (phone: string) => /^[0-9]{10}$/.test(phone);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    let valid = true;
    const newErrors = { phoneNumber: "", password: "" };

    if (!phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
      valid = false;
    } else if (!validatePhoneNumber(phoneNumber)) {
      newErrors.phoneNumber = "Enter a valid 10-digit number";
      valid = false;
    }

    if (!password) {
      newErrors.password = "Password is required";
      valid = false;
    }

    setErrors(newErrors);
    if (!valid) return;

    setLoading(true);
    try {
      const res = await apiPost(endPoints.LOGIN, {
        mobileNumber: phoneNumber,
        password: password,
      });
      if (res.success) {
        toast.success("Welcome back!");        
        localStorage.setItem("authToken", res?.token);        
        dispatch(setUser({ ...res?.user, token: res?.token }));
        dispatch(login());
      } else {
        toast.error(res.data?.message || "Invalid credentials");
      }
    } catch (error: any) {
      toast.error(error.data?.message || "Server error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans selection:bg-indigo-100">
      {/* Branding Section (Left) - Using Modern Indigo/Violet Gradient */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center relative shadow-2xl bg-[#0f172a]">
        {/* Dynamic Abstract Background Shapes */}
        <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-indigo-600 rounded-full blur-[120px] opacity-20 animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-blue-600 rounded-full blur-[120px] opacity-20"></div>
        
        {/* Main Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#4f46e5] via-[#3730a3] to-[#1e1b4b] opacity-90"></div>
        
        <div className="relative z-10 text-center p-16">
          <div className="inline-block p-4 rounded-2xl bg-white/10 backdrop-blur-lg border border-white/20 mb-8 shadow-2xl">
             <h1 className="text-6xl font-black text-white tracking-tighter">iA</h1>
          </div>
          <h1 className="text-5xl font-extrabold text-white mb-6 tracking-tight">iAssets Admin</h1>
          <p className="text-indigo-100/80 text-xl font-light max-w-sm mx-auto leading-relaxed italic">
            "Precision in valuation, excellence in management."
          </p>
          
          <div className="mt-16 flex flex-wrap justify-center gap-4">
             {["Secure", "Real-time", "Scalable"].map(item => (
               <div key={item} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md text-white/90 text-sm font-medium">
                 <div className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_10px_#4ade80]"></div>
                 {item}
               </div>
             ))}
          </div>
        </div>
      </div>

      {/* Form Section (Right) */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-24 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-10 text-left">
            <div className="lg:hidden mb-6">
                <span className="text-3xl font-black text-indigo-600">iAssets</span>
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-3">Login</h2>
            <p className="text-slate-500 font-medium">Access your administrative dashboard.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Phone Number</label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 group-focus-within:bg-indigo-50 transition-colors">
                    <FaPhoneAlt className="text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                </div>
                <input
                  type="tel"
                  placeholder="Mobile number"
                  maxLength={10}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                  className={`w-full pl-16 pr-4 py-4 bg-white border-2 rounded-2xl transition-all outline-none 
                    ${errors.phoneNumber ? "border-red-200 bg-red-50/30" : "border-slate-100 focus:border-indigo-500 focus:shadow-[0_0_20px_rgba(79,70,229,0.1)]"}`}
                />
              </div>
              {errors.phoneNumber && <p className="text-xs text-red-500 font-bold ml-1">{errors.phoneNumber}</p>}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center ml-1">
                <label className="text-sm font-bold text-slate-700">Password</label>
                <button type="button" className="text-xs text-indigo-600 font-extrabold hover:text-indigo-800 transition-colors">Forgot Password?</button>
              </div>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 group-focus-within:bg-indigo-50 transition-colors">
                    <FaLock className="text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full pl-16 pr-14 py-4 bg-white border-2 rounded-2xl transition-all outline-none 
                    ${errors.password ? "border-red-200 bg-red-50/30" : "border-slate-100 focus:border-indigo-500 focus:shadow-[0_0_20px_rgba(79,70,229,0.1)]"}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 font-bold ml-1">{errors.password}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-4 rounded-2xl text-white font-bold text-lg tracking-tight transition-all shadow-xl 
                ${loading 
                  ? "bg-slate-300 cursor-not-allowed" 
                  : "bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-200 active:scale-[0.98] shadow-indigo-100"}`}
            >
              {loading ? (
                  <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Authenticating...
                  </div>
              ) : "Sign In"}
            </button>
          </form>

          <div className="mt-16 text-center">
             <p className="text-sm text-slate-400 font-medium">
               &copy; 2026 iAssets Ecosystem. All rights reserved.
             </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;